/**
 * The telemetry vocabulary — event names and the rules for choosing one.
 *
 * Separated from `signals.js` because that module imports Firebase, and the
 * regression suite is deliberately zero-dependency: it runs on Node's built-in
 * runner so it works in the assistant's Linux shell where `node_modules` holds
 * macOS binaries. Anything reachable from a test has to be a pure module.
 *
 * It is also the honest split. Which events exist, and which one a given
 * outcome maps to, is a product decision. Getting a document into Firestore is
 * plumbing. Only one of those is worth testing, and it was previously welded to
 * the other.
 *
 * `signals.js` re-exports everything here, so `import { EVENTS } from
 * "../lib/signals"` keeps working.
 */

export const EVENTS = {
  INTAKE_PARSED: "intake.parsed",
  RESOLVED: "assessment.resolved",
  EXPOSURE_SCORED: "exposure.scored",
  WALL_SHOWN: "wall.shown",
  ACCOUNT_CREATED: "wall.account_created",
  NEWSLETTER_OPT_IN: "wall.newsletter_opt_in",
  PATHWAY_CHOSEN: "pathways.chosen",
  LEARN_OPENED: "learn.opened",

  // ── The longitudinal loop (R13, ADR-022, ADR-040) ────────────────────────
  //
  // The console had no way to answer the one question a longitudinal product
  // lives on: does anyone come back? Everything above measures a single visit.
  //
  // Direction is encoded in the EVENT NAME, not in a field. The rules carry a
  // strict field allowlist (ADR-038), so `deltaBand` would mean editing and
  // redeploying firestore.rules; the event string is already validated and is
  // already how the console counts. `occupationCode` rides along because it is
  // allowlisted, so movement can still be broken down by role.
  ASSESSMENT_REPEAT: "assessment.repeat",
  DELTA_DOWN: "delta.down",
  DELTA_UP: "delta.up",
  DELTA_FLAT: "delta.flat",
  DELTA_ROLE_CHANGED: "delta.role_changed",
  DELTA_METHOD_CHANGED: "delta.method_changed",

  // Consequential, and a deletion rate is a trust signal worth watching.
  ACCOUNT_EXPORTED: "account.exported",
  ACCOUNT_DELETED: "account.deleted",
};

/**
 * Which event a delta verdict records.
 *
 * Mirrors ADR-040's refusal to subtract: an incomparable verdict never lands in
 * an up/down bucket, because a cohort statistic that averages role changes into
 * "exposure is falling" is the same lie as the arrow, at scale.
 */
export const deltaEvent = (verdict) => {
  if (!verdict) return null;
  if (!verdict.comparable) {
    return verdict.reason === "different-role"
      ? EVENTS.DELTA_ROLE_CHANGED
      : EVENTS.DELTA_METHOD_CHANGED;
  }
  const c = verdict.overall?.change;
  return c < 0 ? EVENTS.DELTA_DOWN : c > 0 ? EVENTS.DELTA_UP : EVENTS.DELTA_FLAT;
};
